import {MipLevelRegion, Region, Regions} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/image-op-descriptor"
import {assertNoBatching} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/utils/image-op-utils"
import {ImageOpContextHal} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/image-op-context-hal"
import {definition} from "@app/textures/texture-editor/operator-stack/image-op-system/image-op-nodes/primitive/resize/definition"
import {CreateDataArgs, ImageOpHal} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/hal/image-op-hal"
import {RegionLockedHalImageRef} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/hal/region-locked-hal-image-ref"
import {HalImage} from "@cm/hal"
import {Size2Like} from "@cm/math"
import {InterpolationMode} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/hal/resize-helper-hal"
import {ImageOpHalParameters, UpdateImageOptions} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/hal/image-op-hal-types"
import {AnyImageOpHalData, CtorArgs, ImageOpHalData} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/hal/image-op-hal-data"
import {UPDATE_COMPLEXITY_HIGH, UPDATE_COMPLEXITY_VERY_LOW} from "@app/textures/texture-editor/operator-stack/image-op-system/helpers/update-complexity"

// const MIN_MIP_LEVEL_BIAS = 0 // don't allow negative mip level bias as it can blow up memory usage; NOTE: this can cause severe rendering artifacts because stacking convolves (e.g. gradient removal) on top of each other will sub-sample/convolve/super-sample, but with this limit in place the requested mip will just get coarser and coarser, resulting in way too low base-images to be requested
const MIN_MIP_LEVEL_BIAS = Number.NEGATIVE_INFINITY
const MAX_MIP_LEVEL_BIAS = Number.POSITIVE_INFINITY

type Definition = typeof definition

export class ImageOpResizeHal extends ImageOpHal<Definition, ImageOpResizeHalData> {
    constructor(ctx: ImageOpContextHal, parameters: ImageOpHalParameters<Definition>) {
        super(ctx)
        this.setParameters(parameters)
    }

    protected override createData(args: CreateDataArgs<Definition, ImageOpResizeHalData>): ImageOpResizeHalData {
        const forwardSourceImage =
            args.parameters.resultSize.width === args.parameters.sourceImage.descriptor.width &&
            args.parameters.resultSize.height === args.parameters.sourceImage.descriptor.height
        return new ImageOpResizeHalData(
            {
                ...args,
                descriptor: {
                    width: args.parameters.resultSize.width,
                    height: args.parameters.resultSize.height,
                    channels: args.parameters.sourceImage.descriptor.channels,
                },
                outputImageReuse: forwardSourceImage ? "no-output-image" : "reuse-if-same-descriptor",
                updateComplexity: forwardSourceImage ? UPDATE_COMPLEXITY_VERY_LOW : UPDATE_COMPLEXITY_HIGH,
            },
            forwardSourceImage,
        )
    }
}

class ImageOpResizeHalData extends ImageOpHalData<Definition> {
    constructor(
        args: CtorArgs<Definition, ImageOpResizeHalData>,
        private forwardSourceImage: boolean,
    ) {
        super(args)
    }

    protected override async updateHalImage(mipLevelRegion: MipLevelRegion, options?: UpdateImageOptions): Promise<RegionLockedHalImageRef> {
        if (this.forwardSourceImage) {
            return this.parameters.sourceImage.getUpdatedHalImage(mipLevelRegion, options)
        } else {
            return super.updateHalImage(mipLevelRegion, options)
        }
    }

    protected override async writeRegion(outputImage: HalImage, mipLevelRegion: MipLevelRegion, options?: UpdateImageOptions): Promise<void> {
        const {sourceImage, resultSize, interpolationMode, addressMode} = this.parameters
        sourceImage.addRef("Resize.writeRegion - sourceImage")
        let sourceHalImage: RegionLockedHalImageRef | undefined
        try {
            // update source image
            const sourceImageMipLevelBias = this.computeSourceImageMipLevelBias(sourceImage)
            const sourceMipLevel = Math.max(0, Math.min(sourceImage.numMipLevels - 1, mipLevelRegion.mipLevel + sourceImageMipLevelBias))
            const sourceRegion = this.determineSourceRegion(sourceImage, resultSize, interpolationMode, mipLevelRegion, sourceMipLevel)
            sourceHalImage = await sourceImage.getUpdatedHalImage(
                {
                    ...sourceRegion,
                    mipLevel: sourceMipLevel,
                },
                options,
            )
            if (!sourceHalImage) {
                throw new Error("Failed to update source image regions")
            }

            assertNoBatching(sourceImage.descriptor)

            this.ctx.resizeHelper.resize({
                interpolationMode,
                source: sourceHalImage.halImage,
                sourceMipLevel: sourceMipLevel,
                target: outputImage,
                targetMipLevel: mipLevelRegion.mipLevel,
                clipRegion: mipLevelRegion,
                addressMode,
            })
        } finally {
            sourceHalImage?.release()
            sourceImage.release("Resize.writeRegion - sourceImage")
        }
    }

    protected override onSourceRegionsInvalidated(_sourceImage: AnyImageOpHalData, sourceRegions: Regions) {
        const targetRegions = sourceRegions.flatMap((sourceRegion): Region => {
            const haloWidth = this.ctx.resizeHelper.getHaloWidth(this.parameters.interpolationMode)
            const regionScaleX = this.parameters.resultSize.width / this.parameters.sourceImage.descriptor.width
            const regionScaleY = this.parameters.resultSize.height / this.parameters.sourceImage.descriptor.height
            const x0 = Math.floor((sourceRegion.x - haloWidth) * regionScaleX)
            const y0 = Math.floor((sourceRegion.y - haloWidth) * regionScaleY)
            const x1 = Math.ceil((sourceRegion.x + sourceRegion.width + haloWidth * 2) * regionScaleX)
            const y1 = Math.ceil((sourceRegion.y + sourceRegion.height + haloWidth * 2) * regionScaleY)
            return {
                x: x0,
                y: y0,
                width: x1 - x0,
                height: y1 - y0,
            }
        })
        this.invalidateRegions(targetRegions)
    }

    private determineSourceRegion(
        sourceImage: AnyImageOpHalData,
        resultSize: Size2Like,
        interpolationMode: InterpolationMode,
        mipLevelRegion: MipLevelRegion,
        sourceMipLevel: number,
    ): Region {
        const sourceMipLevelBias = sourceMipLevel - mipLevelRegion.mipLevel
        const sourceMipLevelBiasScale = Math.pow(2, -sourceMipLevelBias)
        const sourceRegionScaleX = (sourceImage.descriptor.width / resultSize.width) * sourceMipLevelBiasScale
        const sourceRegionScaleY = (sourceImage.descriptor.height / resultSize.height) * sourceMipLevelBiasScale
        const interpolationMargin = this.ctx.resizeHelper.getHaloWidth(interpolationMode)
        const x0 = Math.floor(mipLevelRegion.x * sourceRegionScaleX) - interpolationMargin
        const y0 = Math.floor(mipLevelRegion.y * sourceRegionScaleY) - interpolationMargin
        const x1 = Math.ceil((mipLevelRegion.x + mipLevelRegion.width) * sourceRegionScaleX) + interpolationMargin
        const y1 = Math.ceil((mipLevelRegion.y + mipLevelRegion.height) * sourceRegionScaleY) + interpolationMargin
        return {
            x: x0,
            y: y0,
            width: x1 - x0,
            height: y1 - y0,
        }
    }

    private computeSourceImageMipLevelBias(sourceImage: AnyImageOpHalData) {
        const resultSize = this.parameters.resultSize
        const sourceMipLevelBiases = {
            x: Math.floor(Math.log2(sourceImage.descriptor.width / resultSize.width / this.shrinkMinHeadroomScale)),
            y: Math.floor(Math.log2(sourceImage.descriptor.height / resultSize.height / this.shrinkMinHeadroomScale)),
        }
        // If all axes are downscaling (all biases ≥ 0), use Math.min — conservative, avoids requesting a source
        // that's too coarse for any axis.
        // If any axis is upscaling (some bias < 0), use Math.max — prevents the most-upscaled axis from dragging
        // non-upscaled axes to mip 0, which inflates their source regions by 2^M due to mip level floor-clamping.
        // (All-negative biases like uniform postResize are unchanged: max(-5,-5) = min(-5,-5) = -5, preserving the
        // "reset" that prevents mip level escalation in stacked blurs.)
        const anyUpscaling = sourceMipLevelBiases.x < 0 || sourceMipLevelBiases.y < 0
        let mipLevelBias = anyUpscaling ? Math.max(sourceMipLevelBiases.x, sourceMipLevelBiases.y) : Math.min(sourceMipLevelBiases.x, sourceMipLevelBiases.y)
        mipLevelBias = Math.max(MIN_MIP_LEVEL_BIAS, Math.min(MAX_MIP_LEVEL_BIAS, mipLevelBias))
        return mipLevelBias
    }

    // NOTE: even though lib-vips says this headroom should be at least 2x the target resolution, practically it has been observed that this can still cause severe moiré patterns (e.g. see "Baltimore Black" - Specular Strength map)
    private readonly shrinkMinHeadroomScale = 1 // the down-sampled (pre-filtered) resolution will be between the target size and twice the target size before final resample
}
